import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Search, Flag, Loader2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getMyFriends } from '../../redux/user/userAction';
import { sendMessage } from '../../redux/chat/chatReducer';
import { useSocket } from '../../hooks/useSocket';
import { getTransactionReceipt, reportTransaction } from '../../redux/wallet/walletAction';
import ModalWrapper from '../ModalWrapper';
import BDLogo from '../../Assets/images/BB.png';
import BDCoin from '../../Assets/icons/coin.png'
import defaultPhoto from '../../assests/defaultImage.png';
import {
  formatTransactionType,
  formatPaymentMethod,
  getStatusColor,
  getTransactionAmountInfo,
  isMoneyInflow
} from '../../utils/transactionFormatter';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const PRESET_REPORT_REASONS = [
  'Payment not reflecting on my balance',
  "Didn't get paid",
  'Wrong amount charged',
  'Duplicate transaction',
  'Unauthorized transaction',
];

export default function TransactionDetailsModal({ transaction, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { socket, isConnected } = useSocket();
  const myFriends = useSelector((state) => state.user?.myFriends || []);
  const userData = useSelector((state) => state.user?.userData);

  const [localTransaction, setLocalTransaction] = useState(transaction);

  const [showShareModal, setShowShareModal] = useState(false);
  const [showSendToModal, setShowSendToModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [sendStatus, setSendStatus] = useState('idle'); // idle | sending | success | error
  const [receiptData, setReceiptData] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const [selectedReasons, setSelectedReasons] = useState([]);
  const [otherChecked, setOtherChecked] = useState(false);
  const [customReason, setCustomReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);

  useEffect(() => {
    setLocalTransaction(transaction);
  }, [transaction]);

  const transferParty = isMoneyInflow(localTransaction?.type)
    ? (localTransaction?.transferDetails?.sender ?? localTransaction?.metadata?.sender)
    : (localTransaction?.transferDetails?.receiver ?? localTransaction?.metadata?.receiver)
  const amountInfo = getTransactionAmountInfo(localTransaction)
  const { userId, firstName, lastName } = transferParty ?? {}

  useEffect(() => {
    dispatch(getMyFriends());
  }, [dispatch]);

  const typeInfo = formatTransactionType(localTransaction?.type);
  const isWithdrawal = localTransaction?.type === 'WITHDRAWAL_REQUEST';
  const statusColor = getStatusColor(localTransaction?.status);

  // Format friends data to match modal needs
  const formattedFriends = myFriends.map((friend) => {
    const profileImage = friend?.profilePhoto?.[0]?.photoPublicUrl || defaultPhoto;
    return {
      userId: friend?.userId,
      id: friend?.userId,
      firstName: friend?.firstName || '',
      lastName: friend?.lastName || '',
      name: `${friend?.firstName || ''} ${friend?.lastName || ''}`.trim() || friend?.UserName,
      avatar: profileImage,
    };
  });

  const filteredUsers = formattedFriends.filter((u) =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const shareOptions = [
    { name: 'BD', icon: BDLogo, type: 'image', color: 'bg-[#c9a227]' },
    { name: 'Pinterest', icon: '📌', type: 'emoji', color: 'bg-pink-200' },
    { name: 'Telegram', icon: '✈️', type: 'emoji', color: 'bg-blue-100' },
    { name: 'SMS', icon: '💬', type: 'emoji', color: 'bg-teal-100' },
    { name: 'Linkedin', icon: 'in', type: 'text', color: 'bg-blue-200' },
    { name: 'Gmail', icon: '✉️', type: 'emoji', color: 'bg-green-100' },
  ];

  const buildReceiptMessage = () => {
    const source = receiptData || localTransaction;
    const info = getTransactionAmountInfo(source);
    const lines = [
      `🧾 ${typeInfo.label} Receipt`,
      `${info.primaryLabel}: ${info.display}`,
    ];
    if (info.paidDisplay) lines.push(`${info.paidLabel}: ${info.paidDisplay}`);
    lines.push(`Status: ${source?.status ? source.status.charAt(0) + source.status.slice(1).toLowerCase() : 'N/A'}`);
    lines.push(`Date: ${moment(source?.createdAt).format('MMM Do YYYY, HH:mm')}`);
    lines.push(`Transaction ID: ${source?.transactionId || 'N/A'}`);
    return lines.join('\n');
  };

  const handleOpenShare = async () => {
    setReceiptLoading(true);
    try {
      const data = await getTransactionReceipt(localTransaction.transactionId);
      setReceiptData(data);
    } catch (error) {
      // Fall back silently to the data already on hand for this transaction
    } finally {
      setReceiptLoading(false);
      setShowShareModal(true);
    }
  };

  const handleSendReceipt = async () => {
    const friend = formattedFriends.find((u) => u.id === selectedUser);
    if (!friend) return;

    if (!socket || !isConnected) {
      toast.error('Connection error. Please try again.');
      return;
    }

    setSendStatus('sending');
    const messageContent = buildReceiptMessage();
    const messageId = `receipt_${localTransaction.transactionId}_${Date.now()}`;
    const nowIso = new Date().toISOString();

    try {
      await new Promise((resolve, reject) => {
        socket.emit('chat message', {
          senderId: userData?.userId,
          receiverId: friend.id,
          content: messageContent,
          reply: null,
        }, (response) => {
          if (response && response.status === 'success') {
            resolve(response);
          } else {
            reject(new Error(response?.message || 'Failed to send'));
          }
        });

        setTimeout(() => reject(new Error('Request timeout')), 10000);
      });

      const roomId = `${friend.id}_${userData?.userId}`;
      const message = {
        messageId,
        content: messageContent,
        type: 'text',
        senderId: userData?.userId,
        receiverId: friend.id,
        timestamp: nowIso,
        localCreatedAt: nowIso,
        createdAt: nowIso,
        chatType: 'text',
        sent: true,
        delivered: false,
        read: false,
      };
      const listData = {
        user: {
          userId: friend.id,
          firstName: friend.firstName,
          lastName: friend.lastName,
          profilePhoto: [],
        },
        room: roomId,
        lastMessage: {
          content: messageContent,
          createdAt: nowIso,
          localCreatedAt: nowIso,
          chatType: 'text',
          messageId,
          senderId: userData?.userId,
          receiverId: friend.id,
        },
        unreadCount: 1,
        archivedStatus: false,
      };

      dispatch(sendMessage({ roomId, message, listData }));
      setSendStatus('success');
      toast.success(`Receipt sent to ${friend.name}!`);
      setTimeout(() => {
        setShowSendToModal(false);
        setSelectedUser(null);
        setSendStatus('idle');
      }, 900);
    } catch (error) {
      setSendStatus('error');
      toast.error(error.message || 'Failed to send receipt');
    }
  };

  const toggleReason = (reason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  const resetReportForm = () => {
    setSelectedReasons([]);
    setOtherChecked(false);
    setCustomReason('');
    setReportDetails('');
  };

  const handleSubmitReport = async () => {
    const finalReasons = [...selectedReasons];
    if (otherChecked && customReason.trim()) finalReasons.push(customReason.trim());

    if (finalReasons.length === 0) {
      toast.error('Please select or describe a reason');
      return;
    }

    setReportSubmitting(true);
    try {
      const data = await reportTransaction(localTransaction.transactionId, {
        reasons: finalReasons,
        reason: finalReasons[0],
        details: reportDetails.trim(),
      });

      setLocalTransaction((prev) => ({
        ...prev,
        isReported: true,
        report: data?.report ?? prev.report,
      }));
      toast.success('Transaction reported successfully');
      setShowReportModal(false);
      resetReportForm();
      queryClient.invalidateQueries({ queryKey: ['wallet', 'history'] });
    } catch (error) {
      toast.error(error.message || 'Failed to report transaction');
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <>
      {/* ================= MAIN TRANSACTION MODAL ================= */}
      <ModalWrapper onClose={onClose} showClose={false}>
        <div className="p-4">
          <div className="bg-[#2a2a2a] rounded-t-lg p-3">
            <p className="text-sm text-gray-400 flex items-center gap-2">
              <span>{typeInfo.icon}</span>
              <span>{isWithdrawal ? 'Withdrawal Details' : 'Transaction Details'}</span>
            </p>
          </div>

          <div className="text-center py-6">
            <p className="text-gray-400 text-sm mb-1">{amountInfo.primaryLabel}</p>
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-3xl font-bold flex items-center gap-1">
                {amountInfo.isCoin && <img src={BDCoin} className="w-8 aspect-square" alt="Coin" />}
                {amountInfo.display}
              </span>
            </div>
            <div className={`flex items-center justify-center gap-1 ${statusColor}`}>
              <Check className="w-4 h-4" />
              <span className="capitalize text-sm">{localTransaction?.status?.toLowerCase()}</span>
            </div>
          </div>

          <div className="space-y-4 mb-6 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">
                {isMoneyInflow(localTransaction?.type) ? 'From Account' : 'To Account'}
              </span>
              <span onClick={userId ? () => navigate(`/user/${userId}`) : undefined} className={`text-right ${userId ? "hover:underline cursor-pointer" : ""}`}>
                {firstName ? `${firstName} ${lastName || ''}`.trim() : 'Unknown'}
              </span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-400">Type</span>
              <span>{typeInfo.label}</span>
            </div>

            {amountInfo.paidDisplay && (
              <div className="flex justify-between">
                <span className="text-gray-400">{amountInfo.paidLabel}</span>
                <span>{amountInfo.paidDisplay}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-400">Payment Method</span>
              <span>{formatPaymentMethod(localTransaction?.paymentMethod)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-400">Date & Time</span>
              <span>{moment(localTransaction?.createdAt).format("MMM Do YYYY, HH:mm:ss")}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-400">Transaction ID</span>
              <span className="text-xs font-mono truncate w-32 text-right">
                {localTransaction?.transactionId || 'N/A'}
              </span>
            </div>
          </div>

          <button
            onClick={handleOpenShare}
            disabled={receiptLoading}
            className="w-full bg-[#c9a227] hover:bg-[#d4ad2e] disabled:opacity-70 text-black font-semibold py-3 rounded-lg transition-colors mb-3 flex items-center justify-center gap-2"
          >
            {receiptLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            Share as PDF
          </button>

          {localTransaction?.isReported ? (
            <div className="w-full flex items-center justify-center gap-1.5 text-xs text-gray-500 py-1">
              <Flag className="w-3.5 h-3.5 text-red-400" />
              <span>
                Reported{localTransaction?.report?.latest?.reportedAt ? ` ${moment(localTransaction.report.latest.reportedAt).fromNow()}` : ''}
              </span>
            </div>
          ) : (
            <button
              onClick={() => setShowReportModal(true)}
              className="w-full text-center text-gray-400 hover:text-white transition-colors text-sm"
            >
              Report Transaction
            </button>
          )}
        </div>
      </ModalWrapper>

      {/* ================= SHARE RECEIPT MODAL ================= */}
      {createPortal(
        <AnimatePresence>
          {showShareModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
              onClick={() => setShowShareModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-[#4a4a4a] rounded-2xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold">
                    Share Receipt
                  </h3>
                  <button
                    onClick={() => setShowShareModal(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {shareOptions.map((option) => (
                    <motion.button
                      key={option.name}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        if (option.name === 'BD') {
                          setShowShareModal(false);
                          setShowSendToModal(true);
                        }
                      }}
                      className="flex flex-col items-center gap-2"
                    >
                      <div
                        className={`w-14 h-14 ${option.color} rounded-full flex items-center justify-center`}
                      >
                        {option.type === 'image' ? (
                          <img
                            src={option.icon}
                            alt={option.name}
                            className="w-7 h-7 object-contain"
                          />
                        ) : option.type === 'text' ? (
                          <span className="text-blue-700 font-bold text-lg">
                            {option.icon}
                          </span>
                        ) : (
                          <span className="text-2xl">
                            {option.icon}
                          </span>
                        )}
                      </div>
                      <span className="text-sm">{option.name}</span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* ================= SEND TO MODAL ================= */}
      {createPortal(
        <AnimatePresence>
          {showSendToModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
              onClick={() => (sendStatus !== 'sending') && setShowSendToModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-[#4a4a4a] rounded-2xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    Send to Friends
                  </h3>
                  <button
                    onClick={() => setShowSendToModal(false)}
                    disabled={sendStatus === 'sending'}
                    className="text-gray-400 hover:text-white disabled:opacity-50"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search friends..."
                    className="w-full bg-[#5a5a5a] rounded-lg p-3 pl-10 text-white placeholder-gray-400 focus:outline-none text-sm"
                  />
                </div>

                <p className="text-xs text-gray-400 mb-2">
                  Friends {filteredUsers.length > 0 && `(${filteredUsers.length})`}
                </p>

                {filteredUsers.length === 0 ? (
                  <div className="py-8 text-center text-gray-400">
                    <span className="text-sm">
                      {searchQuery ? 'No friends found' : 'You have no friends yet'}
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                    {filteredUsers.map((user) => (
                      <motion.button
                        key={user.id}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => {
                          if (sendStatus === 'sending') return;
                          setSendStatus('idle');
                          setSelectedUser(
                            user.id === selectedUser ? null : user.id
                          )
                        }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${selectedUser === user.id
                          ? 'bg-[#c9a227] text-black'
                          : 'bg-[#5a5a5a] hover:bg-[#6a6a6a]'
                          }`}
                      >
                        <img
                          src={user.avatar}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <span className="text-sm font-medium truncate">{user.name}</span>
                      </motion.button>
                    ))}
                  </div>
                )}

                <button
                  onClick={handleSendReceipt}
                  disabled={!selectedUser || filteredUsers.length === 0 || sendStatus === 'sending' || sendStatus === 'success'}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors text-sm flex items-center justify-center gap-2 ${selectedUser && filteredUsers.length > 0
                    ? 'bg-[#c9a227] hover:bg-[#d4ad2e] text-black'
                    : 'bg-[#3a3a3a] text-gray-500 cursor-not-allowed'
                    }`}
                >
                  {sendStatus === 'sending' && <Loader2 className="w-4 h-4 animate-spin" />}
                  {sendStatus === 'sending' ? 'Sending...' : sendStatus === 'success' ? 'Sent!' : 'Share Receipt'}
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* ================= REPORT TRANSACTION MODAL ================= */}
      {createPortal(
        <AnimatePresence>
          {showReportModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[2147483647] p-4"
              onClick={() => !reportSubmitting && setShowReportModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-[#4a4a4a] rounded-2xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Flag className="w-5 h-5 text-red-400" />
                    Report Transaction
                  </h3>
                  <button
                    onClick={() => !reportSubmitting && setShowReportModal(false)}
                    disabled={reportSubmitting}
                    className="text-gray-400 hover:text-white disabled:opacity-50"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  Transaction ID: <span className="font-mono">{localTransaction?.transactionId}</span>
                </p>

                <p className="text-sm text-gray-300 mb-2">What went wrong?</p>
                <div className="space-y-2 mb-4">
                  {PRESET_REPORT_REASONS.map((reason) => (
                    <label
                      key={reason}
                      className="flex items-center gap-2 bg-[#3a3a3a] rounded-lg p-2.5 cursor-pointer text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedReasons.includes(reason)}
                        onChange={() => toggleReason(reason)}
                        className="w-4 h-4 rounded border-gray-500 bg-[#2a2a2a] text-[#c9a227] focus:ring-[#c9a227]"
                      />
                      <span>{reason}</span>
                    </label>
                  ))}

                  <label className="flex items-center gap-2 bg-[#3a3a3a] rounded-lg p-2.5 cursor-pointer text-sm">
                    <input
                      type="checkbox"
                      checked={otherChecked}
                      onChange={(e) => setOtherChecked(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-500 bg-[#2a2a2a] text-[#c9a227] focus:ring-[#c9a227]"
                    />
                    <span>Other</span>
                  </label>

                  {otherChecked && (
                    <input
                      type="text"
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="Describe your reason..."
                      className="w-full bg-[#5a5a5a] rounded-lg p-2.5 text-white placeholder-gray-400 focus:outline-none text-sm"
                    />
                  )}
                </div>

                <p className="text-sm text-gray-300 mb-2">Additional details (optional)</p>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder="Add any extra context that could help us investigate..."
                  rows={3}
                  className="w-full bg-[#5a5a5a] rounded-lg p-3 text-white placeholder-gray-400 focus:outline-none text-sm mb-6 resize-none"
                />

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => !reportSubmitting && setShowReportModal(false)}
                    disabled={reportSubmitting}
                    className="flex-1 py-3 bg-[#3a3a3a] hover:bg-[#4a4a4a] rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitReport}
                    disabled={reportSubmitting || (selectedReasons.length === 0 && !(otherChecked && customReason.trim()))}
                    className="flex-1 py-3 bg-[#c9a227] hover:bg-[#d4ad2e] disabled:opacity-50 disabled:cursor-not-allowed text-black rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    {reportSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
